import {
  KatButton,
  KatDropdown,
  KatIcon,
  KatInput,
  KatLabel,
  KatModal,
} from "@amzn/katal-react";
import classnames from "classnames";
import React, { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CopyToClipboardButton from "src/components/Pages/DocumentInbound/CopyToClipboardButton";
import {
  DEFAULT_TRANSLATION_CONFIG,
  DEFAULT_TRANSLATION_NS,
} from "src/components/TranslatedField/TranslationKey";
import { ComplianceUserActionType } from "src/components/UserActions/types";
import publishMetric, { MetricAction } from "src/metrics";
import styles from "src/styles.module.scss";

export interface ForceRejectionConfirmationModalProps {
  setIsVisible: any;
  handleAction: any;
  documentId: string;
  rejectionReasons: string[];
}

const ForceRejectionConfirmationModal: FC<
  ForceRejectionConfirmationModalProps
> = ({ handleAction, setIsVisible, documentId, rejectionReasons }) => {
  const [confirmationId, setConfirmationId] = useState("");
  const [rejectionReasonKey, setRejectionReasonKey] = useState("");
  const [isForceRejectionAllowed, setIsForceRejectionAllowed] = useState(false);
  const { t } = useTranslation(
    DEFAULT_TRANSLATION_NS,
    DEFAULT_TRANSLATION_CONFIG
  );

  const rejectReasonsDropdownItems = rejectionReasons.map(
    (reason) =>
      ({
        name: t(reason),
        value: reason,
      }) as KatalDropdownOption
  );

  const isRejectReasonEnabled = rejectReasonsDropdownItems.length !== 0;

  const handleOnChange = ({ target: { value } }: any) => {
    setConfirmationId(value.trim());
  };

  const handleOnChangerejectionReasonKey = ({ target: { value } }: any) => {
    setRejectionReasonKey(value.trim());
  };

  const handleCancel = () => {
    publishMetric(MetricAction.FORCE_REJECTION_CANCEL);
    setConfirmationId("");
    setIsVisible(false);
  };

  function handleReject() {
    publishMetric(MetricAction.FORCE_REJECTION_CONFIRM);
    setIsVisible(false);
    if (isRejectReasonEnabled) {
      handleAction(ComplianceUserActionType.FORCE_REJECT, {
        rejectionReasonKey,
      });
      return;
    }
    handleAction(ComplianceUserActionType.FORCE_REJECT);
  }

  useEffect(() => {
    if (isRejectReasonEnabled) {
      setIsForceRejectionAllowed(
        documentId === confirmationId && rejectionReasonKey !== ""
      );
      return;
    }
    setIsForceRejectionAllowed(documentId === confirmationId);
  }, [confirmationId, documentId, rejectionReasonKey, isRejectReasonEnabled]);

  return (
    <KatModal
      visible={true}
      title={t("aic_doc_page_force_rejection_modal_title")}
      onClose={() => handleCancel()}
    >
      <div
        className={classnames(
          styles.flex_row_nonwrap,
          styles.pb_8,
          styles.relative
        )}
      >
        <KatLabel
          text={t("aic_doc_page_force_rejection_modal_main_text", {
            documentId: documentId,
          })}
          variant={"constraint"}
          className={classnames(styles.mr_08)}
        />
        <CopyToClipboardButton
          label="document-id"
          value={documentId}
          translationPrefix="aic_doc_page_breadcrumb_document_list_document_id_popover"
        />
        <KatLabel text={"?"} variant={"constraint"} />
      </div>

      <div className={classnames(styles.flex_direction_row, styles.pb_8)}>
        <KatIcon name={"alert-fill"} size={"tiny"} />
        <KatLabel text={t("aic_doc_page_force_rejection_modal_warning")} />
      </div>

      {isRejectReasonEnabled && (
        <div>
          <KatLabel
            text={t(
              "aic_doc_page_force_rejection_modal_rejection_reason_subtext"
            )}
            variant={"constraint"}
          />
          <KatDropdown
            placeholder={t("aic_doc_page_force_rejection_rejection_reason")}
            options={rejectReasonsDropdownItems}
            onChange={handleOnChangerejectionReasonKey}
          />
          <br></br>
        </div>
      )}

      <KatLabel
        text={t("aic_doc_page_force_rejection_modal_subtext")}
        variant={"constraint"}
      />
      <KatInput
        value={confirmationId}
        id={"input"}
        onInput={handleOnChange}
        type="text"
        placeholder={t("aic_doc_page_force_rejection_doc_id")}
        className={classnames(styles.pt_8)}
      />

      <div className={classnames(styles.buttons_at_the_end)}>
        <KatButton
          variant={"danger"}
          onClick={() => handleReject()}
          disabled={!isForceRejectionAllowed}
        >
          {t("aic_doc_page_force_rejection_modal_action")}
        </KatButton>

        <KatButton variant={"primary"} onClick={() => handleCancel()}>
          {t("aic_doc_page_po_header_edit_cancel")}
        </KatButton>
      </div>
    </KatModal>
  );
};

export default ForceRejectionConfirmationModal;
